import React, { useEffect, useMemo, useRef, useState } from "react"

import { useNavigate } from "react-router-dom"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import {
    COMMON_ONE_VERIFY_POLICIES_CONFIG,
    PoliciesListType,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import {
    POLICIES_CONFIG,
    PolicyHeaderDragDirection,
} from "../../config/policies.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { CoverageHistoryHelper } from "../../helpers/coverageHistory.helper.ts"
import { PoliciesListHelper } from "../../helpers/policiesList.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { usePoliciesList } from "../../hooks/policiesList.hook.ts"
import { useAppSelector } from "../../store/selectors/app.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { selectedPracticeConfigurationSelector } from "../../store/selectors/selectedPracticeConfiguration.selector.ts"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { PoliciesListComponent } from "../policies/policiesList/policiesList.component"
import { PoliciesListHeaderComponent } from "../policies/policiesList/policiesListHeader.component"
import { TaskAssignmentAssigneePopupComponent } from "../popups/taskAssignmentAssigneePopup.component"
import { TaskAssignmentUnassignPopupComponent } from "../popups/taskAssignmentUnassignPopup.component.tsx"
import { BulkTaskAssignmentControlComponent } from "./bulkTaskAssignmentControl.component"

export const TaskAssignmentsComponent: React.FunctionComponent = () => {
    useScrollFix()

    const navigate = useNavigate()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const selectedPracticeConfiguration = useAppSelector(
        selectedPracticeConfigurationSelector,
    )

    const profile = useAppSelector(profileSelector)

    // Check if user has required permissions and feature flags
    // Logic matches menu config combinedPermissions:
    // Allow if: (user has manageTaskAssignments) OR (user has workTaskAssignments AND ONE_VERIFY_BILLER_SELF_ASSIGN flag is enabled)
    // Both require ONE_VERIFY_TASKS flag to be enabled
    const hasTasksFeatureFlag = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_TASKS,
        undefined,
        undefined,
    )

    const billerSelfAssignFlagEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_BILLER_SELF_ASSIGN,
        undefined,
        undefined,
    )

    const hasManagePermission = useHasPermissions(
        OneVerifyPermission.manageTaskAssignments,
        undefined,
        false,
    )

    const hasWorkPermission = useHasPermissions(
        OneVerifyPermission.workTaskAssignments,
        undefined,
        false,
    )

    // Check if user has access via either path:
    // 1. Has manageTaskAssignments permission, OR
    // 2. Has workTaskAssignments permission AND biller self-assign feature flag
    // Both require ONE_VERIFY_TASKS flag to be enabled
    useEffect(() => {
        // Don't redirect if data is still loading
        if (
            hasTasksFeatureFlag === null ||
            hasManagePermission === null ||
            hasWorkPermission === null ||
            billerSelfAssignFlagEnabled === null
        ) {
            return
        }

        const redirectUrl = ROUTES_CONFIG.profileUrl

        if (!hasTasksFeatureFlag) {
            navigate(redirectUrl)

            return
        }

        const hasAccessViaWork =
            hasWorkPermission && billerSelfAssignFlagEnabled

        if (!hasManagePermission && !hasAccessViaWork) {
            navigate(redirectUrl)
        }
    }, [
        navigate,
        hasTasksFeatureFlag,
        hasManagePermission,
        hasWorkPermission,
        billerSelfAssignFlagEnabled,
    ])

    const {
        // state
        selectedPolicies,
        setSelectedPolicies,
        // functions
        onPoliciesSelected,
        onPoliciesDeselected,
        onDeselectAllPolicies,
    } = usePoliciesList()

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    // Biller-only mode: user doesn't have manage permission AND feature flag is enabled
    const isBillerOnlyMode = useMemo(() => {
        if (
            hasManagePermission === null ||
            billerSelfAssignFlagEnabled === null
        ) {
            return false
        }

        return !hasManagePermission && billerSelfAssignFlagEnabled
    }, [hasManagePermission, billerSelfAssignFlagEnabled])

    // Sets list type based on workflow management version
    const listType = useMemo<PoliciesListType | null>(() => {
        if (!selectedPracticeConfiguration) {
            return null
        }

        return COMMON_ONE_VERIFY_POLICIES_CONFIG.taskAssignmentsListTypeVariant[
            selectedPracticeConfiguration.workflowManagementVersion
        ]
    }, [selectedPracticeConfiguration?.workflowManagementVersion])

    const [isAssignmentPopupActive, setIsAssignmentPopupActive] =
        useState<boolean>(false)

    const [isUnassignPopupActive, setIsUnassignPopupActive] =
        useState<boolean>(false)

    // This component controls them and passes to list for display and header for modifications
    const [columnsConfiguration, setColumnsConfiguration] = useState<
        IPolicyColumnConfiguration[]
    >([])

    // Change columns configurations once practice role modalities change
    useEffect(() => {
        if (!selectedPracticeRole || !listType) {
            return
        }

        setColumnsConfiguration(
            PoliciesListHelper.getColumnsConfiguration(
                listType,
                selectedPracticeRole.availableModalities,
            ),
        )
    }, [selectedPracticeRole?.availableModalities, listType])

    const onSaveColumnsConfiguration = (
        columns: IPolicyColumnConfiguration[],
    ) => {
        if (!listType) {
            return
        }

        setColumnsConfiguration(columns)

        PoliciesListHelper.saveColumnsConfigurations(
            listType,
            columns,
            selectedPracticeRole?.availableModalities,
        )
    }

    const onChangeColumnOrder = (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => {
        const newColumns = PoliciesListHelper.changeSingleColumnOrdering(
            columnsConfiguration,
            columnLabelMoveSource,
            columnLabelMoveTarget,
            direction,
        )

        onSaveColumnsConfiguration(newColumns)
    }

    const isSinglePolicySelectable = (
        item: IOneVerifyCoverageCheckHistory,
    ): boolean => {
        if (!CoverageHistoryHelper.getPolicyIdentificationData(item)) {
            return false
        }

        // If not in biller-only mode, allow selection if no assignment exists
        if (!isBillerOnlyMode) {
            return true
        }

        // In biller-only mode:
        // - Allow if no assignment exists
        // - Allow if assignment has done status AND is assigned to current user
        const assignment = item.policy?.latestAssignment

        if (!assignment) {
            return true
        }

        const isDoneStatus =
            assignment.status &&
            COMMON_ONE_VERIFY_POLICIES_CONFIG.doneAssignmentStatuses.includes(
                assignment.status,
            )

        const isAssignedToSelf =
            profile?.id && assignment.assignee?.id === profile.id

        return !!(isDoneStatus && isAssignedToSelf)
    }

    if (!listType) {
        return <SpinnerElement containerClassName="mt-[32px] text-center" />
    }

    return (
        <div className="pb-[128px] relative">
            <PageHelmetElement
                title={POLICIES_CONFIG.headerTitleMapping[listType]}
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <PoliciesListHeaderComponent
                className="mb-[24px]"
                listType={listType}
                setSelectedPolicies={setSelectedPolicies}
                scrollableTableRef={scrollableTableRef}
                columnsConfiguration={columnsConfiguration}
                onColumnsConfigurationChange={onSaveColumnsConfiguration}
                withExport
                withSavingFilterPresets
            />

            {columnsConfiguration.length ? (
                <PoliciesListComponent
                    scrollableElementRef={scrollableTableRef}
                    listType={listType}
                    sortEnabled={true}
                    columns={columnsConfiguration}
                    policiesSelectable={true}
                    selectedItems={selectedPolicies}
                    onSelectedItems={onPoliciesSelected}
                    onDeselectedItems={onPoliciesDeselected}
                    onDeselectedAllItems={onDeselectAllPolicies}
                    onChangeColumnOrder={onChangeColumnOrder}
                    isSinglePolicySelectable={isSinglePolicySelectable}
                />
            ) : null}

            <BulkTaskAssignmentControlComponent
                listType={listType}
                onCancel={onDeselectAllPolicies}
                coverageChecks={selectedPolicies}
                withBulkStatus={!isBillerOnlyMode}
                withBulkPriority={!isBillerOnlyMode}
                withBulkAssign={!!hasManagePermission || isBillerOnlyMode}
                isBillerOnlyMode={isBillerOnlyMode}
                onBulkAssign={() => {
                    setIsAssignmentPopupActive(true)
                }}
                onBulkUnassign={() => {
                    setIsUnassignPopupActive(true)
                }}
            />

            {isAssignmentPopupActive && (
                <TaskAssignmentAssigneePopupComponent
                    coverageChecksToAssign={selectedPolicies}
                    policiesListType={PoliciesListType.TASK_ASSIGNMENTS}
                    onDone={() => {
                        onDeselectAllPolicies()
                        setIsAssignmentPopupActive(false)
                    }}
                    isActive
                    isBillerOnlyMode={isBillerOnlyMode}
                />
            )}

            {isUnassignPopupActive && (
                <TaskAssignmentUnassignPopupComponent
                    coverageChecks={selectedPolicies}
                    policiesListType={listType}
                    onDone={() => {
                        onDeselectAllPolicies()
                        setIsUnassignPopupActive(false)
                    }}
                    isActive
                />
            )}
        </div>
    )
}
